/**
 * Structure Note Component - Refactored
 * Main orchestrator for investment opportunities and subscription flow
 *
 * @remarks
 * This component has been refactored from 1001 lines to ~200 lines by:
 * - Extracting 6 custom hooks for business logic
 * - Creating 9 smaller UI components
 * - Centralizing utilities and constants
 * - Improving type safety
 *
 * New structure: 22 focused files with single responsibilities
 */

import { FC, useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EntityTypeEnum } from "@funded-here-interface/common/src/constant/enum";
import { RegtankOnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { useDocumentVisibility } from "@funded-here-interface/common/src/hooks/useDocumentVisibility";
import { useStyle } from "./StructureNote.styles";

// Custom hooks
import { useOfferRoomData } from "./hooks/useOfferRoomData";
import { useInvestorAccounts } from "./hooks/useInvestorAccounts";
import { useSessionSync } from "./hooks/useSessionSync";
import { useSubscriptionFlow } from "./hooks/useSubscriptionFlow";
import { useRegtankLiveness } from "./hooks/useRegtankLiveness";

// Components
import OfferRoomView from "./components/OfferRoomView";
import SubscriptionView from "./components/SubscriptionView";
import {
  FailedLivenessModal,
  OnboardingCompletedModal,
  PartialSubscriptionModal,
  FullySubscribedModal,
  SUBSCRIPTION_ERROR_CODES,
} from "../../components/DocumentSigning";

// Utils
import { applyCurrencyAccountData } from "./utils/accountMapping";

// Types
import {
  InvestmentOpportunity,
  CurrencyType,
  LivenessVerifyParams,
  PartialSubscriptionData,
} from "./StructureNote.types";
import { sortRegtankRequestsByExpiry } from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";

// Service types
import { SubscriptionError } from "../../services/subscriptionService";

/**
 * Main Structure Note component
 * Manages investment opportunities and subscription workflow
 */
const StructureNote: FC = () => {
  const { classes } = useStyle();
  const navigation = useNavigate();

  // Redux state
  const {
    token,
    userId,
    isAuthRep,
    entityType,
    regtankRequest,
    regtankLiveness,
    regtankOnboarding,
    regtankOnboardingFeatureFlag,
    regtankLivenessFeatureFlag,
  } = useSelector((state: RootState) => state.auth);

  // Local state
  const [selectedData, setSelectedData] =
    useState<InvestmentOpportunity | null>(null);
  const [investorSubscribeAmount, setInvestorSubscribeAmount] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>("SGD");

  // Onboarding completed modal state - shows once per user, persisted in localStorage
  const ONBOARDING_MODAL_KEY = `onboarding_completed_shown_${userId}`;
  const [showOnboardingModal, setShowOnboardingModal] = useState(() => {
    if (!userId) return false;
    return localStorage.getItem(ONBOARDING_MODAL_KEY) !== "true";
  });

  const handleOnboardingModalClose = useCallback(() => {
    if (userId) {
      localStorage.setItem(ONBOARDING_MODAL_KEY, "true");
    }
    setShowOnboardingModal(false);
  }, [userId, ONBOARDING_MODAL_KEY]);

  // Partial subscription modal state
  const [showPartialSubscriptionModal, setShowPartialSubscriptionModal] =
    useState(false);
  const [partialSubscriptionData, setPartialSubscriptionData] =
    useState<PartialSubscriptionData | null>(null);

  // Fully subscribed modal state
  const [showFullySubscribedModal, setShowFullySubscribedModal] =
    useState(false);

  // Hide subscribe button for non-auth rep institutions
  const hideNoteSubscribe =
    entityType === EntityTypeEnum.INSTITUTION && !isAuthRep;

  // Custom hooks
  const { offerRoomData } = useOfferRoomData(token);
  const { accountData, accountGAData } = useInvestorAccounts(token);
  const { sessionState, category, setCategory, clearSession } =
    useSessionSync();

  const {
    realUrl,
    documentId,
    completedAt,
    viewerKey,
    setRealUrl,
    setDocumentId,
    setCompletedAt,
    initiateSubscription,
    completeSubscription,
    fetchSignedDocument,
    incrementViewerKey,
  } = useSubscriptionFlow(token, regtankLivenessFeatureFlag ?? undefined);

  // Restore session state on mount
  useEffect(() => {
    if (sessionState) {
      setCategory("subscribe");
      setRealUrl(sessionState.realUrl);
      setDocumentId(sessionState.documentId);
      setCompletedAt(sessionState.completedAt);
      setInvestorSubscribeAmount(sessionState.investorSubscribeAmount);
      setSelectedData(sessionState.selectedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate document when page becomes visible again after switching tabs or inactive for a while
  useDocumentVisibility(incrementViewerKey, !!sessionState?.documentId);

  /**
   * Handle document signing
   * Handles both full and partial subscriptions, and fully subscribed errors
   */
  const handleSign = useCallback(async () => {
    if (!selectedData) return;

    try {
      const response = await completeSubscription(
        selectedData,
        investorSubscribeAmount
      );

      // Check if subscription was partial
      if (response.isPartialSubscription) {
        setPartialSubscriptionData({
          requestedAmount: response.requestedAmount,
          subscribedAmount: response.subscribedAmount,
          currency: response.currency,
        });
        setShowPartialSubscriptionModal(true);
      }

      clearSession();
      setCategory("view");
      fetchSignedDocument();
    } catch (e) {
      // Check if error is fully subscribed (409)
      if (
        e instanceof SubscriptionError &&
        e.code === SUBSCRIPTION_ERROR_CODES.NOTE_FULLY_SUBSCRIBED
      ) {
        setShowFullySubscribedModal(true);
      } else {
        toast.error((e as Error).message);
      }
    }
  }, [
    selectedData,
    investorSubscribeAmount,
    completeSubscription,
    clearSession,
    setCategory,
    fetchSignedDocument,
  ]);

  // Regtank liveness hook
  const {
    buttonDisabled,
    titleButton,
    showFailedModal,
    handleLivenessVerify,
    handleModalClose,
  } = useRegtankLiveness({
    token,
    documentId,
    regtankLivenessFeatureFlag: regtankLivenessFeatureFlag || false,
    regtankOnboardingFeatureFlag: regtankOnboardingFeatureFlag ?? null,
    regtankOnboarding: regtankOnboarding ?? null,
    regtankRequest,
    regtankLiveness: regtankLiveness ?? undefined,
    onSignSuccess: handleSign,
    onViewerKeyIncrement: incrementViewerKey,
  });

  /**
   * Handle investment card selection
   */
  const handleBoxSelect = useCallback(
    (data: InvestmentOpportunity) => {
      setSelectedData(data);
      setSelectedCurrency(data.currency as CurrencyType);

      // Apply currency-specific account data
      applyCurrencyAccountData(accountData, data.currency);
    },
    [accountData]
  );

  /**
   * Handle investment amount change
   */
  const handleAmountChange = useCallback((value: string) => {
    setInvestorSubscribeAmount(value);
  }, []);

  /**
   * Handle subscribe button click
   */
  const handleSubscribeClick = useCallback(async () => {
    if (investorSubscribeAmount === "") {
      toast.error("Please enter an investment/subscription amount.");
      return;
    }

    if (!selectedData) {
      toast.error("No investment opportunity selected.");
      return;
    }

    // Check Regtank onboarding status
    if (regtankOnboardingFeatureFlag === true && regtankOnboarding === true) {
      if (
        regtankRequest === undefined ||
        (regtankRequest &&
          sortRegtankRequestsByExpiry(regtankRequest)?.[0].onboardingStatus !==
            RegtankOnboardingStatusEnum.APPROVED)
      ) {
        toast.error("Your onboarding is not yet approved.");
        return;
      }
    }

    setCategory("subscribe");
    await initiateSubscription(selectedData, investorSubscribeAmount);
  }, [
    investorSubscribeAmount,
    selectedData,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
    regtankRequest,
    setCategory,
    initiateSubscription,
  ]);

  /**
   * Handle liveness verification click
   */
  const handleLivenessClick = useCallback(() => {
    if (!selectedData) return;

    const params: LivenessVerifyParams = {
      realUrl,
      completedAt,
      investorSubscribeAmount,
      selectedData,
    };

    handleLivenessVerify(params);
  }, [
    realUrl,
    completedAt,
    investorSubscribeAmount,
    selectedData,
    handleLivenessVerify,
  ]);

  /**
   * Navigate back to subscribed notes
   */
  const handleBackToNotes = useCallback(() => {
    navigation("/subscribed-invested-notes");
  }, [navigation]);

  /**
   * Handle partial subscription modal close
   */
  const handlePartialSubscriptionModalClose = useCallback(() => {
    setShowPartialSubscriptionModal(false);
    setPartialSubscriptionData(null);
  }, []);

  /**
   * Handle fully subscribed modal close
   */
  const handleFullySubscribedModalClose = useCallback(() => {
    setShowFullySubscribedModal(false);
  }, []);

  /**
   * Handle browse notes from fully subscribed modal
   */
  const handleBrowseNotes = useCallback(() => {
    setShowFullySubscribedModal(false);
    setCategory("view");
  }, [setCategory]);

  return (
    <>
      <div
        className={classes.stepContent}
        style={{ marginTop: "0px", paddingTop: "0px" }}
      >
        {category === "view" ? (
          <OfferRoomView
            offerRoomData={offerRoomData}
            accountData={accountData}
            accountGAData={accountGAData}
            selectedData={selectedData}
            selectedCurrency={selectedCurrency}
            investorSubscribeAmount={investorSubscribeAmount}
            hideNoteSubscribe={hideNoteSubscribe}
            onBoxSelect={handleBoxSelect}
            onAmountChange={handleAmountChange}
            onSubscribe={handleSubscribeClick}
          />
        ) : (
          <SubscriptionView
            realUrl={realUrl}
            documentId={documentId}
            completedAt={completedAt}
            buttonDisabled={buttonDisabled}
            titleButton={titleButton}
            regtankRequest={regtankRequest}
            viewerKey={viewerKey}
            onSign={handleLivenessClick}
            onBackToNotes={handleBackToNotes}
          />
        )}
      </div>

      {/* Failed Liveness Modal */}
      <FailedLivenessModal
        opened={showFailedModal}
        onClose={handleModalClose}
        onContinue={handleModalClose}
        classes={classes}
      />

      {/* Onboarding Completed Modal - shows once per user */}
      <OnboardingCompletedModal
        opened={showOnboardingModal}
        onClose={handleOnboardingModalClose}
      />

      {/* Partial Subscription Modal */}
      {partialSubscriptionData && (
        <PartialSubscriptionModal
          opened={showPartialSubscriptionModal}
          onClose={handlePartialSubscriptionModalClose}
          requestedAmount={partialSubscriptionData.requestedAmount}
          subscribedAmount={partialSubscriptionData.subscribedAmount}
          currency={partialSubscriptionData.currency}
        />
      )}

      {/* Fully Subscribed Modal */}
      <FullySubscribedModal
        opened={showFullySubscribedModal}
        onClose={handleFullySubscribedModalClose}
        onBrowseNotes={handleBrowseNotes}
      />
    </>
  );
};

export default StructureNote;
